import { useState, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Container, Row, Col, Spinner, Alert } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DocsSidebar from "../../components/Docs/DocsSidebar";
import { SeoTags } from "../../components/Head/SeoTags";
import "./DocsPage.scss";

const DocsPage = () => {
  const { "*": docPath } = useParams();
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMarkdown = async () => {
      setLoading(true);
      setError(null);

      try {
        const fileName = `${docPath}.md`;
        const response = await fetch(`/documentation/${fileName}`);

        if (!response.ok) {
          if (response.status === 404) {
            setError("Documentation page not found");
          } else {
            setError("Failed to load documentation");
          }
          return;
        }

        const text = await response.text();
        setContent(text);
      } catch (err) {
        setError("Failed to load documentation");
        console.error("Error fetching markdown:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMarkdown();
  }, [docPath]);

  if (loading) {
    return (
      <Container className="docs-container">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (error) {
    if (error === "Documentation page not found" && docPath) {
      return <Navigate to="/docs" replace />;
    }

    return (
      <Container className="docs-container">
        <Alert variant="danger" className="mt-5">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  const pageTitle = docPath
    ? docPath.split("/").pop().replace(/-/g, " ")
    : "Documentation";
  const capitalizedTitle = pageTitle
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return (
    <>
      <SeoTags
        title={`${capitalizedTitle} - onBookmarks Documentation`}
        description="Learn how to use onBookmarks effectively with our comprehensive documentation and guides."
        keywords="onBookmarks documentation, guide, tutorial, how-to, bookmarks manager"
        localized={false}
      />

      <Container className="docs-container">
        <Row>
          <Col lg={3} className="docs-sidebar-col">
            <DocsSidebar currentPath={docPath} />
          </Col>
          <Col lg={9} className="docs-content-col">
            <div className="docs-content">
              <div className="markdown-body">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children, ...props }) => {
                      const id = children
                        .toString()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-");
                      return (
                        <h1
                          id={id}
                          className="h2 text-primary-gradient mt-2"
                          {...props}
                        >
                          {children}
                        </h1>
                      );
                    },
                    h2: ({ children, ...props }) => {
                      const id = children
                        .toString()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-");
                      return (
                        <h2
                          id={id}
                          className="h3 border-top pt-3"
                          {...props}
                        >
                          {children}
                        </h2>
                      );
                    },
                    h3: ({ children, ...props }) => {
                      const id = children
                        .toString()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-");
                      return (
                        <h3 id={id} className="h4 text-muted" {...props}>
                          {children}
                        </h3>
                      );
                    },
                    h4: ({ children, ...props }) => {
                      const id = children
                        .toString()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-");
                      return (
                        <h4 id={id} className="h5 mb-2" {...props}>
                          {children}
                        </h4>
                      );
                    },
                    h5: ({ children, ...props }) => {
                      const id = children
                        .toString()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-");
                      return (
                        <h5 id={id} className="h6 mb-2" {...props}>
                          {children}
                        </h5>
                      );
                    },
                    h6: ({ children, ...props }) => {
                      return <strong className="d-block">{children}</strong>;
                    },
                    a: ({ href, children, ...props }) => {
                      // Handle relative links to other docs
                      if (
                        href &&
                        !href.startsWith("http") &&
                        href.endsWith(".md")
                      ) {
                        const docLink = href
                          .replace(".md", "")
                          .replace("./", "");
                        return (
                          <a href={`/docs/${docLink}`} {...props}>
                            {children}
                          </a>
                        );
                      }
                      // External links open in new tab
                      if (href && href.startsWith("http")) {
                        return (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            {...props}
                          >
                            {children}
                          </a>
                        );
                      }
                      return (
                        <a href={href} {...props}>
                          {children}
                        </a>
                      );
                    },
                    img: ({ src, alt, ...props }) => {
                      // Handle relative image paths
                      if (src && !src.startsWith("http")) {
                        return (
                          <img 
                            src={`/docs/${src}`} 
                            alt={alt} 
                            loading="lazy"
                            decoding="async"
                            fetchpriority="low"
                            referrerPolicy="no-referrer"
                            {...props} 
                          />
                        );
                      }
                      return (
                        <img 
                          src={src} 
                          alt={alt} 
                          loading="lazy"
                          decoding="async"
                          fetchpriority="low"
                          referrerPolicy="no-referrer"
                          {...props} 
                        />
                      );
                    },
                    code: ({ inline, className, children, ...props }) => {
                      // Inline code (single backticks)
                      if (inline || !className) {
                        return (
                          <code
                            className={`bg-light px-1 py-0 rounded text-dark ${
                              className || ""
                            }`}
                            style={{ fontSize: "0.9em" }}
                            {...props}
                          >
                            {children}
                          </code>
                        );
                      }
                      // Code blocks (triple backticks)
                      return (
                        <pre
                          className={`bg-dark text-light p-3 rounded ${
                            className || ""
                          }`}
                        >
                          <code {...props}>{children}</code>
                        </pre>
                      );
                    },
                  }}
                >
                  {content}
                </ReactMarkdown>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default DocsPage;
